import { resources } from '@/api/helpers'
import { Api } from '@/types'
import { normalizeArrayResponse } from '@/utils'

async function getResourceCollection<T>(request: Promise<unknown>): Promise<T[]> {
	return normalizeArrayResponse<T>(await request)
}

export async function getTasks(): Promise<Api.Task[]> {
	return getResourceCollection<Api.Task>(resources.get('tasks').json())
}

export async function getTasksByProjectId(projectId: number): Promise<Api.Task[]> {
	return getResourceCollection<Api.Task>(resources.get('tasks', { searchParams: { project_id: projectId } }).json())
}

export async function getEmployees(): Promise<Api.Employee[]> {
	const employees = await getResourceCollection<Api.Employee>(resources.get('employees').json())
	return employees.map(e =>
		Object.assign(e, {
			get name(): string {
				return `${e.first_name} ${e.last_name}`
			},
		})
	)
}

export async function getEmployeeByMyoctansId(userId: number): Promise<Api.Employee> {
	return await resources.get(`employees`, { searchParams: { myoctans_id: userId } }).json()
}

export async function getProjects(): Promise<Api.Project[]> {
	const projects = await getResourceCollection<Api.Project>(resources.get('projects').json())
	return projects.map(e =>
		Object.assign(e, {
			get name(): string {
				return `${e.number || ''}${e.number ? ' - ' : ''}${e.name}`
			},
		})
	)
}

export async function getProjectsWithTask(): Promise<Api.Project[]> {
	const projects = await getResourceCollection<Api.Project>(resources.get('projects?with-task=1').json())
	return projects.map(e =>
		Object.assign(e, {
			get name(): string {
				return `${e.number || ''}${e.number ? ' - ' : ''}${e.name}`
			},
		})
	)
}

export async function getProfessions(): Promise<Api.Profession[]> {
	return getResourceCollection<Api.Profession>(resources.get('professions').json())
}

export async function getSuppliers(): Promise<Api.Supplier[]> {
	return getResourceCollection<Api.Supplier>(resources.get('suppliers').json())
}

export async function getEquipments(): Promise<Api.Equipment[]> {
	return getResourceCollection<Api.Equipment>(resources.get('equipment').json())
}

export async function getMaterials(): Promise<Api.Material[]> {
	return getResourceCollection<Api.Material>(resources.get('materials').json())
}

export async function getBonuses(): Promise<Api.Bonus[]> {
	return getResourceCollection<Api.Bonus>(resources.get('bonuses').json())
}

export async function getVehicles(): Promise<Api.Equipment[]> {
	return getResourceCollection<Api.Equipment>(resources.get('equipment', { searchParams: { category: 'vehicle' } }).json())
}

export async function getDrills(): Promise<Api.Equipment[]> {
	return getResourceCollection<Api.Equipment>(resources.get('equipment', { searchParams: { category: 'drilling-machine' } }).json())
}

export async function getSeismograph(): Promise<Api.Equipment[]> {
	return getResourceCollection<Api.Equipment>(resources.get('equipment', { searchParams: { category: 'seismograph' } }).json())
}

export async function getBorers(): Promise<Api.Equipment[]> {
	return getResourceCollection<Api.Equipment>(resources.get('equipment', { searchParams: { category: 'borer' } }).json())
}

export async function getStubResources(): Promise<Api.Resource[]> {
	return getResourceCollection<Api.Resource>(resources.get('stub').json())
}

export async function getNotifications(): Promise<Notification[]> {
	return getResourceCollection<Notification>(resources.get('notifications').json())
}

export async function getLocations(): Promise<Api.Location[]> {
	return getResourceCollection<Api.Location>(resources.get('locations').json())
}

export async function getPurchaseOrders(): Promise<Api.PurchaseOrder[]> {
	const purchaseOrders = await getResourceCollection<Api.PurchaseOrder>(resources.get('purchase-orders').json())
	return purchaseOrders.map(e =>
		Object.assign(e, {
			get name(): string {
				return `${e.invoice_number}`
			},
		})
	)
}

export async function getAddresses(): Promise<Api.Address[]> {
	return getResourceCollection<Api.Address>(resources.get('addresses').json());
}

export async function getSettings() : Promise<Api.Setting[]> {
	return getResourceCollection<Api.Setting>(resources.get('settings').json());
}
